import {
  Alert,
  AppBar,
  Box,
  Button,
  CssBaseline,
  Menu,
  Toolbar,
  Tooltip,
  Typography,
  tooltipClasses,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect } from "react";
import {
  logEvent,
  logOutHelper,
  useLazyGetRolesQuery,
} from "../features/api/apiSlice";
import {
  removeIssueById,
  selectIssues,
} from "../features/appState/issuesSlice";
import {
  selectCurrentEmail,
  selectCurrentName,
  selectCurrentPatientGroup,
  selectCurrentPatientGroups,
  selectCurrentPatientSubGroup,
  selectCurrentPicture,
  selectCurrentToken,
  setPatientGroup,
  setPatientGroups,
  setPatientSubGroup,
  setWhatsAppVoiceCallEnabledGroups,
} from "../features/auth/authSlice";
import { createStyleSheet, globalStyles } from "../utils/styles";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExitFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import FeedbackIcon from "@mui/icons-material/Feedback";
import EnterFullscreenIcon from "@mui/icons-material/Fullscreen";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";
import AppLogo from "../../assets/hp_logo.png";
import { theme } from "../../theme";
import { LogoutReason } from "../features/api/logging";
import {
  selectHasUnsavedWork,
  setFeedbackDialogInfo,
} from "../features/appState/uxSlice";
import { patientApiSlice } from "../features/data/patientApiSlice";
import { retryIssue } from "../features/middleware";
import { store, useAppDispatch } from "../features/store";
import ClinicianBadge from "./ClinicianBadge";
import IconButton from "./IconButton";
import IssuesList from "./IssuesList";
import LightTooltip from "./LightTooltip";
import PatientGroupSelector from "./PatientGroupSelector";
import UnsavedChangesDialog from "./UnsavedChangesDialog";
import UnsavedWorkGuard from "./UnsavedWorkGuard";
import { getDisplayName, GROUP_DISPLAY_NAME_MAP } from "../utils/strings";

const PrimaryAppBar = () => {
  const isLoggedIn = !!useSelector(selectCurrentToken);
  const userEmail = useSelector(selectCurrentEmail);
  const userName = useSelector(selectCurrentName);
  const picture = useSelector(selectCurrentPicture);
  const patientGroup = useSelector(selectCurrentPatientGroup);
  const patientGroups = useSelector(selectCurrentPatientGroups);
  const patientSubGroup = useSelector(selectCurrentPatientSubGroup);
  const issues = useSelector(selectIssues);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("PrimaryAppBar");
  const token = useSelector(selectCurrentToken);
  const isMobile = useMediaQuery(theme.breakpoints.down("desktop"));
  const [getRoles, getRolesResult] = useLazyGetRolesQuery({
    pollingInterval: 30000,
  });
  const {
    data: rolesData,
    isFetching: isRolesDataFetching,
    isSuccess: isRolesDataSuccess,
  } = getRolesResult;

  // Component state
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [fullscreen, setFullscreen] = React.useState(
    !!document.fullscreenElement
  );
  const [unsavedChangesDialogInfo, setUnsavedChangesDialogInfo] =
    React.useState<{
      open: boolean;
      logout?: boolean;
      group?: string;
      subGroup?: string;
    }>({ open: false });

  // Consistently update state for a users authorized roles
  useEffect(() => {
    if (isRolesDataSuccess) {
      dispatch(setPatientGroups(Object.keys(rolesData!.patient_groups!)));
      dispatch(
        setWhatsAppVoiceCallEnabledGroups(
          Object.entries(rolesData!.patient_groups!)
            .filter(([, group]) => group.whatsapp_voice_call_enabled)
            .map(([groupName]) => groupName)
        )
      );
    }
  }, [dispatch, isRolesDataFetching, isRolesDataSuccess, rolesData]);

  useEffect(() => {
    if (token) {
      getRoles(token);
    }
  }, [getRoles, token]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Callbacks
  const logOut = useCallback(() => {
    logOutHelper(dispatch, patientApiSlice, LogoutReason.UserInitiated);
  }, [dispatch]);
  const changePatientGroup = useCallback(
    (group: string, subGroup?: string) => {
      dispatch(setPatientGroup(group));
      dispatch(setPatientSubGroup(subGroup ?? ""));
      dispatch(patientApiSlice.util.invalidateTags(["TaskBasic"]));
      navigate("/patients", { state: { unsavedWorkDiscarded: true } });
    },
    [dispatch, navigate]
  );
  const handleLogout = useCallback(() => {
    if (selectHasUnsavedWork(store.getState())) {
      setUnsavedChangesDialogInfo({ open: true, logout: true });
    } else {
      logOut();
    }
  }, [logOut]);
  const handleBadgeClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      setMenuAnchor(e.currentTarget);
      e.stopPropagation();
    },
    []
  );
  const handleDropdownClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setMenuAnchor(e.currentTarget);
      e.stopPropagation();
    },
    []
  );
  const handlePatientGroupClick = useCallback(
    (group: string, subGroup?: string) => {
      setMenuAnchor(null);
      if (selectHasUnsavedWork(store.getState())) {
        setUnsavedChangesDialogInfo({ open: true, group, subGroup });
      } else {
        changePatientGroup(group, subGroup);
      }
    },
    [changePatientGroup]
  );
  const handleUnsavedChangesDialogClose = useCallback(() => {
    setUnsavedChangesDialogInfo({ open: false });
  }, []);
  const handleUnsavedChangesDialogDiscard = useCallback(() => {
    setUnsavedChangesDialogInfo({ open: false });
    if (unsavedChangesDialogInfo.logout) {
      logOut();
    } else {
      changePatientGroup(
        unsavedChangesDialogInfo.group!,
        unsavedChangesDialogInfo.subGroup
      );
    }
  }, [unsavedChangesDialogInfo, logOut, changePatientGroup]);

  const handleMenuClose = useCallback((e: React.SyntheticEvent) => {
    setMenuAnchor(null);
    e.stopPropagation();
  }, []);

  const isInDemoGroup = () => {
    const testGroups = [
      /\b\w+-demo\b/i,
      /\btest-\w*\b/i,
      /\btest\b/i,
      /\b2024-09-SouthAfrica-Au\b/i,
    ];
    return testGroups.some((regex) =>
      regex.test(patientGroup?.[userEmail] ?? "")
    );
  };

  const handleRetryFn = useCallback(
    (issueId: string) => {
      const issue = issues.find((i) => i.id === issueId)!;
      retryIssue(dispatch, issue);
    },
    [dispatch, issues]
  );
  const handleCloseFn = useCallback(
    (issueId: string) => {
      dispatch(removeIssueById(issueId));
      logEvent(dispatch, {
        event: "issueRemoved",
        data: {
          issueId,
        },
      });
    },
    [dispatch]
  );

  const rolesPatientGroup = React.useMemo(() => {
    return Object.entries(rolesData?.patient_groups ?? {}).map(
      ([groupKey, groupValue]) => {
        const subGroups = Object.keys(groupValue.subgroups ?? {});

        return {
          groupKey,
          groupValue,
          subGroups,
        };
      }
    );
  }, [rolesData?.patient_groups]);

  return (
    <Box sx={styles.container}>
      <CssBaseline />
      <AppBar position="static" color="transparent" sx={styles.appBar}>
        <Toolbar disableGutters sx={styles.toolbar}>
          <Box component="img" src={AppLogo} alt="Logo" sx={styles.appLogo} />
          {isInDemoGroup() && (
            <Alert sx={{ margin: 5 }} severity="info">
              Using demo group
            </Alert>
          )}
          <Box sx={globalStyles.flexSpace} />
          <Box sx={styles.authContainer}>
            {isLoggedIn ? (
              <>
                {issues.length > 0 ? (
                  <LightTooltip
                    title={
                      <IssuesList
                        issues={issues}
                        onRetryFn={handleRetryFn}
                        onCloseFn={handleCloseFn}
                      />
                    }
                    sx={{
                      [`& .${tooltipClasses.tooltip}`]: {
                        maxWidth: 600,
                      },
                    }}
                    arrow
                  >
                    <div>
                      <Alert severity="warning" sx={styles.issuesAlert}>
                        {t("issuesOccurred", { count: issues.length })}
                      </Alert>
                    </div>
                  </LightTooltip>
                ) : null}
                {patientGroups?.length || 0 ? (
                  <Button
                    onClick={handleDropdownClick}
                    endIcon={<ArrowDropDownIcon />}
                    sx={{ marginRight: 1, textTransform: "none" }}
                  >
                    {patientSubGroup?.[userEmail] ? (
                      <Tooltip
                        title={`${patientGroup?.[userEmail]} > ${patientSubGroup?.[userEmail]}`}
                        placement="bottom"
                      >
                        <span>
                          {getDisplayName(patientGroup?.[userEmail] || "")}
                          <ChevronRightIcon sx={{ fontSize: 16, mx: 0.5 }} />
                          {getDisplayName(patientSubGroup?.[userEmail] || "")}
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        title={
                          patientGroup?.[userEmail] || patientGroups?.[0] || ""
                        }
                        placement="bottom"
                      >
                        <span>
                          {getDisplayName(
                            patientGroup?.[userEmail] ||
                              patientGroups?.[0] ||
                              ""
                          )}
                        </span>
                      </Tooltip>
                    )}
                  </Button>
                ) : null}

                <ClinicianBadge
                  clinician={{ name: userName, picture }}
                  sx={{ marginRight: 1 }}
                  onClick={
                    (rolesPatientGroup?.length || 0) > 1
                      ? handleBadgeClick
                      : undefined
                  }
                />
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={handleMenuClose}
                  slotProps={{ list: { role: "listbox" } }}
                >
                  <PatientGroupSelector
                    rolesPatientGroup={rolesPatientGroup}
                    patientGroup={patientGroup}
                    patientSubGroup={patientSubGroup}
                    userEmail={userEmail}
                    onPatientGroupClick={handlePatientGroupClick}
                  />
                </Menu>
              </>
            ) : null}
            {isLoggedIn ? (
              <>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="button" noWrap>
                    {userName}
                  </Typography>
                  <Button
                    onClick={() => dispatch(setFeedbackDialogInfo(true))}
                    startIcon={<FeedbackIcon sx={styles.feedbackIcon} />}
                    sx={styles.feedbackButton}
                  >
                    {t("shareFeedback")}
                  </Button>
                </Box>
                <Button
                  sx={styles.signOutButton}
                  variant="outlined"
                  onClick={handleLogout}
                >
                  {t("signOut")}
                </Button>
              </>
            ) : null}
          </Box>
          {isMobile || fullscreen ? (
            <IconButton
              icon={
                fullscreen ? <ExitFullscreenIcon /> : <EnterFullscreenIcon />
              }
              tooltip={t("common:toggleFullscreen")}
              size="small"
              sx={styles.fullscreenButton}
              onClick={() =>
                fullscreen
                  ? document.exitFullscreen()
                  : document.body.requestFullscreen()
              }
            />
          ) : null}
        </Toolbar>
      </AppBar>
      <Box sx={styles.outletContainer}>
        <Outlet />
      </Box>
      <UnsavedChangesDialog
        open={unsavedChangesDialogInfo.open}
        onClose={handleUnsavedChangesDialogClose}
        onDiscard={handleUnsavedChangesDialogDiscard}
      />
      <UnsavedWorkGuard />
    </Box>
  );
};

const styles = createStyleSheet({
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
  },
  appBar: {
    boxShadow: 0,
    borderBottom: 1,
    borderBottomColor: "grey.300",
  },
  toolbar: {
    height: "100px",
    px: 3,
    [theme.breakpoints.down("desktop")]: {
      height: "64px",
    },
  },
  appLogo: {
    objectFit: "contain",
    height: "43px",
    [theme.breakpoints.down("desktop")]: { height: "32px" },
  },
  authContainer: {
    display: "flex",
    alignItems: "center",
  },
  issuesAlert: {
    marginRight: 2,
    cursor: "default",
  },
  signOutButton: {
    color: "secondary",
    marginLeft: 2,
  },
  fullscreenButton: {
    marginLeft: 2,
  },
  outletContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "calc(100% - 100px)",
    [theme.breakpoints.down("desktop")]: {
      height: "calc(100% - 64px)",
    },
  },
  feedbackButton: {
    padding: 0,
    minWidth: "auto",
    textTransform: "none",
    textDecoration: "underline",
    fontSize: "12px",
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: 0,

    "& .MuiButton-startIcon": {
      marginRight: "2px",
    },
  },
  feedbackIcon: {
    fontSize: 12,
    color: theme.palette.primary.main,
  },
});

export default PrimaryAppBar;
